interface Params {
  orderItem: Pick<App.OrderItem, 'package' | 'packagePrice' | 'channelMarkupId' | 'reservation' | 'total' | 'dynamicPricingSurchargeAmount'>
  duration: number
  checkIn?: string
  checkOut?: string
  packageUniqueKey?: string
  isLpc: boolean
  isDerbysoftDateChange?: boolean
  price: number
  memberPrice: number
  useMemberPrice: boolean
  surcharge: number
  operation: 'change-dates' | 'add-extra-guests'
  extraGuestSurcharge: number
  extraGuestSurchargePayableAtProperty: boolean
}

type ProtectedFlashDateChangeParams = Pick<Params,
  'orderItem' | 'duration' | 'checkIn' | 'checkOut' | 'packageUniqueKey' | 'isLpc' |
  'isDerbysoftDateChange' | 'surcharge' | 'operation' | 'extraGuestSurcharge' | 'extraGuestSurchargePayableAtProperty'
>

/** Keep the paid accommodation amount when only the dates of a direct Flash booking change. */
export function getProtectedFlashDateChangePrice({
  orderItem,
  duration,
  checkIn,
  checkOut,
  packageUniqueKey,
  isLpc,
  isDerbysoftDateChange = false,
  surcharge,
  operation,
  extraGuestSurcharge,
  extraGuestSurchargePayableAtProperty,
}: ProtectedFlashDateChangeParams): number | undefined {
  const newPackageSelected = orderItem.package.duration !== duration ||
    (packageUniqueKey !== undefined && orderItem.package.uniqueKey !== packageUniqueKey)
  if (operation !== 'change-dates' || newPackageSelected || isLpc || isDerbysoftDateChange ||
    !orderItem.reservation || orderItem.reservation.surchargePaidDirectToVendor || extraGuestSurchargePayableAtProperty) {
    return undefined
  }

  if (checkIn && checkOut && checkIn === orderItem.reservation.startDate && checkOut === orderItem.reservation.endDate) {
    return orderItem.total
  }

  // The reservation's non-EGS surcharge includes internal fees; dynamic pricing
  // is stored separately and must also be replaced by the new payable surcharge.
  const originalNonGuestSurcharge = (orderItem.reservation.surcharge ?? 0) - (orderItem.reservation.extraGuestSurcharge ?? 0)
  return orderItem.total + surcharge - extraGuestSurcharge - originalNonGuestSurcharge - (orderItem.dynamicPricingSurchargeAmount ?? 0)
}

/** The amendment's payment basis before subtracting the original total; supplier quotes stay unchanged. */
export default function getHotelDateChangePrice(params: Params): number {
  const {
    orderItem,
    duration,
    checkIn,
    checkOut,
    packageUniqueKey,
    isLpc,
    isDerbysoftDateChange = false,
    price,
    memberPrice,
    useMemberPrice,
    surcharge,
  } = params
  const newPackageSelected = orderItem.package.duration !== duration ||
    (packageUniqueKey !== undefined && orderItem.package.uniqueKey !== packageUniqueKey)

  if (!newPackageSelected && checkIn && checkOut &&
    checkIn === orderItem.reservation?.startDate && checkOut === orderItem.reservation?.endDate) {
    return orderItem.total
  }

  // Supplier cancel/rebook retains the purchased base for the same Flash package.
  const repricePackage = newPackageSelected || isLpc
  const priceToUse = repricePackage || (orderItem.channelMarkupId && !isDerbysoftDateChange) ? price : orderItem.packagePrice
  const memberPriceToUse = repricePackage ? memberPrice : orderItem.packagePrice
  const nextPrice = useMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse

  const protectedPrice = getProtectedFlashDateChangePrice(params)
  if (protectedPrice !== undefined) {
    return protectedPrice
  }

  return nextPrice + surcharge
}
